/*
 * Region and language suggestion popup (snippets/region-selector.liquid).
 *
 * Suggests, never redirects. Opens only when the detected country differs from the current one, or the best
 * language for the visitor differs from the page language. "Best" is the browser's preferred language when it's
 * published, otherwise the country's main language (COUNTRY_LANGUAGES), otherwise the shop's primary language.
 * A choice or a dismissal is remembered for 90 days per detected country and language.
 */

const STORAGE_KEY = 'bw_region_selector'
const LEGACY_STORAGE_KEY = 'bw_korea_region_dismissed'
const SESSION_KEY = 'bw_region_selector_detected'
const REMEMBER_MS = 90 * 24 * 60 * 60 * 1000
const LEGACY_REMEMBER_MS = 7 * 24 * 60 * 60 * 1000
const CONSENT_READY_TIMEOUT_MS = 5000

const SPANISH = 'ES MX AR BO CL CO CR CU DO EC SV GT HN NI PA PY PE PR UY VE'.split(' ')

const COUNTRY_LANGUAGES = {
  KR: ['ko'],
  JP: ['ja'],
  CN: ['zh-CN'],
  SG: ['zh-CN'],
  TW: ['zh-TW'],
  HK: ['zh-TW'],
  MO: ['zh-TW'],
  DE: ['de'],
  AT: ['de'],
  FR: ['fr'],
  BE: ['fr'],
  LU: ['fr'],
  MC: ['fr'],
  CH: ['de', 'fr'],
  ...Object.fromEntries(SPANISH.map(country => [country, ['es']])),
}

const TRADITIONAL_CHINESE_SUBTAGS = ['hant', 'tw', 'hk', 'mo']

function readJSON(storage, key) {
  try {
    return JSON.parse(storage.getItem(key)) || null
  } catch (error) {
    return null
  }
}

function writeJSON(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch (error) {
    // Storage can be full or blocked (private mode). The popup still works, it just isn't remembered.
  }
}

function debugParam() {
  const params = new URLSearchParams(window.location.search)
  return params.get('region_selector') ?? params.get('korea_region_selector')
}

class RegionSelector extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog')
    this.form = this.querySelector('form')
    this.languageSelect = this.querySelector('select[name="language_code"]')
    this.countrySelect = this.querySelector('select[name="country_code"]')
    this.fields = this.querySelector('#RegionSelectorFields')
    this.changeButton = this.querySelector('[data-change]')
    this.choice = this.querySelector('[data-choice]')
    this.footnote = this.querySelector('[data-footnote]')

    this.currentCountry = this.dataset.country
    this.currentLanguage = this.dataset.language
    this.primaryLanguage = this.dataset.primaryLanguage || this.currentLanguage
    this.dutiesCountry = this.dataset.dutiesCountry

    if (!this.dialog || !this.form) return

    this.sortCountries()
    this.bindEvents()

    const debug = debugParam()
    if (debug === '0') return

    const start = () => this.init(debug === '1')
    if (document.readyState === 'complete') {
      start()
    } else {
      window.addEventListener('load', start, { once: true })
    }
  }

  get languages() {
    if (!this.languageSelect) return [this.currentLanguage]
    return Array.from(this.languageSelect.options, option => option.value)
  }

  get countries() {
    if (!this.countrySelect) return [this.currentCountry]
    return Array.from(this.countrySelect.options, option => option.value)
  }

  async init(force) {
    const detectedCountry = await this.detectCountry()
    const bestLanguage = this.bestLanguage(detectedCountry)
    const suggestedCountry =
      detectedCountry && this.countries.includes(detectedCountry) ? detectedCountry : this.currentCountry

    this.memoryKey = `${detectedCountry || this.currentCountry}:${bestLanguage}`

    if (!force) {
      const mismatch = suggestedCountry !== this.currentCountry || bestLanguage !== this.currentLanguage
      if (!mismatch || this.isRemembered(detectedCountry)) return
    }

    if (this.languageSelect) this.languageSelect.value = bestLanguage
    if (this.countrySelect) this.countrySelect.value = suggestedCountry
    this.updateChoice()

    if (!force) await this.waitForConsentBanner()
    this.open()
  }

  async detectCountry() {
    const cached = readJSON(window.sessionStorage, SESSION_KEY)
    if (cached && 'country' in cached) return cached.country

    let country = null
    try {
      const root = window.Shopify?.routes?.root || '/'
      const response = await fetch(`${root}browsing_context_suggestions.json?country[enabled]=true`)
      if (response.ok) {
        const data = await response.json()
        country = data?.detected_values?.country?.handle?.toUpperCase() || null
        writeJSON(window.sessionStorage, SESSION_KEY, { country })
      }
    } catch (error) {
      // Detection failed: fall back to language-only suggestions.
    }
    return country
  }

  matchLanguage(tag) {
    if (!tag) return null
    const published = this.languages
    const byLowerCase = new Map(published.map(code => [code.toLowerCase(), code]))
    const lower = tag.toLowerCase()
    if (byLowerCase.has(lower)) return byLowerCase.get(lower)

    const [base, ...subtags] = lower.split('-')
    if (base === 'zh') {
      const traditional = subtags.some(subtag => TRADITIONAL_CHINESE_SUBTAGS.includes(subtag))
      return byLowerCase.get(traditional ? 'zh-tw' : 'zh-cn') || null
    }
    if (byLowerCase.has(base)) return byLowerCase.get(base)
    return published.find(code => code.toLowerCase().split('-')[0] === base) || null
  }

  bestLanguage(country) {
    const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language]
    const preferred = this.matchLanguage(browserLanguages[0])
    if (preferred) return preferred

    const candidates = [...(COUNTRY_LANGUAGES[country] || [])]
    if (candidates.length > 1) {
      const browserBases = browserLanguages.map(tag => (tag || '').toLowerCase().split('-')[0])
      candidates.sort((a, b) => {
        const indexA = browserBases.indexOf(a)
        const indexB = browserBases.indexOf(b)
        return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
      })
    }
    for (const candidate of candidates) {
      const match = this.matchLanguage(candidate)
      if (match) return match
    }

    return this.matchLanguage(this.primaryLanguage) || this.currentLanguage
  }

  isRemembered(detectedCountry) {
    const memory = readJSON(window.localStorage, STORAGE_KEY) || {}
    const timestamp = memory[this.memoryKey]
    if (timestamp && Date.now() - timestamp < REMEMBER_MS) return true

    const legacy = readJSON(window.localStorage, LEGACY_STORAGE_KEY)
    return detectedCountry === 'KR' && !!legacy?.timestamp && Date.now() - legacy.timestamp < LEGACY_REMEMBER_MS
  }

  remember() {
    if (!this.memoryKey) return
    const now = Date.now()
    const memory = readJSON(window.localStorage, STORAGE_KEY) || {}
    Object.keys(memory).forEach(key => {
      if (now - memory[key] >= REMEMBER_MS) delete memory[key]
    })
    memory[this.memoryKey] = now
    writeJSON(window.localStorage, STORAGE_KEY, memory)
  }

  // Don't stack overlays: wait until the visitor has answered the cookie consent banner.
  waitForConsentBanner() {
    return new Promise(resolve => {
      const gwConsent = window.gwConsent
      if (!gwConsent) return resolve()

      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        resolve()
      }
      const readyTimeout = setTimeout(finish, CONSENT_READY_TIMEOUT_MS)

      gwConsent.whenReady(customerPrivacy => {
        clearTimeout(readyTimeout)
        try {
          if (customerPrivacy?.shouldShowBanner?.() && customerPrivacy.getTrackingConsent?.() === 'no_interaction') {
            gwConsent.onChange(finish)
            return
          }
        } catch (error) {
          // Fall through and show the popup.
        }
        finish()
      })
    })
  }

  sortCountries() {
    if (!this.countrySelect) return
    let collator
    try {
      collator = new Intl.Collator(document.documentElement.lang || undefined, { sensitivity: 'base' })
    } catch (error) {
      collator = new Intl.Collator()
    }
    const selected = this.countrySelect.value
    const options = Array.from(this.countrySelect.options)
    options.sort((a, b) => collator.compare(a.textContent.trim(), b.textContent.trim()))
    this.countrySelect.append(...options)
    this.countrySelect.value = selected
  }

  bindEvents() {
    this.querySelectorAll('[data-dismiss]').forEach(button => {
      button.addEventListener('click', () => this.dismiss())
    })

    this.dialog.addEventListener('click', event => {
      if (event.target === this.dialog) this.dismiss()
    })

    this.dialog.addEventListener('cancel', () => this.remember())
    this.dialog.addEventListener('close', () => this.onClose())
    this.dialog.addEventListener('keydown', event => this.trapFocus(event))

    this.form.addEventListener('submit', () => this.remember())

    this.changeButton?.addEventListener('click', () => this.toggleFields())
    this.languageSelect?.addEventListener('change', () => this.updateChoice())
    this.countrySelect?.addEventListener('change', () => this.updateChoice())
  }

  updateChoice() {
    const languageOption = this.languageSelect?.selectedOptions[0]
    const countryOption = this.countrySelect?.selectedOptions[0]
    const country = this.countrySelect ? this.countrySelect.value : this.currentCountry

    if (this.choice && (languageOption || countryOption)) {
      const parts = []
      if (languageOption) {
        const language = document.createElement('span')
        language.lang = languageOption.value
        language.textContent = languageOption.textContent.trim()
        parts.push(language)
      }
      if (countryOption) parts.push(countryOption.textContent.trim())
      this.choice.replaceChildren(...parts.flatMap((part, index) => (index ? [' · ', part] : [part])))
    }

    if (this.footnote) this.footnote.hidden = country !== this.dutiesCountry
  }

  toggleFields(open) {
    if (!this.fields || !this.changeButton) return
    open ??= this.fields.hidden
    this.fields.hidden = !open
    this.changeButton.setAttribute('aria-expanded', String(open))
    if (open) this.fields.querySelector('select')?.focus()
  }

  open() {
    if (this.dialog.open) return
    this.previousFocus = document.activeElement
    this.dialog.showModal()
    document.documentElement.classList.add('region-selector-open')
  }

  dismiss() {
    this.remember()
    if (this.dialog.open) this.dialog.close()
  }

  onClose() {
    document.documentElement.classList.remove('region-selector-open')
    if (this.previousFocus?.isConnected && document.activeElement === document.body) this.previousFocus.focus()
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return
    const focusable = Array.from(
      this.dialog.querySelectorAll(
        'button, select, input:not([type="hidden"]), a[href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter(element => !element.disabled && !element.closest('[hidden]'))
    if (!focusable.length) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
}

if (!customElements.get('region-selector')) {
  customElements.define('region-selector', RegionSelector)
}
